<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4 hover:shadow-md transition-shadow">
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {{ etiqueta }}
        </p>
        <p class="mt-2 text-2xl font-bold" :class="obtenerClaseColor">
          {{ montoFormateado }}
        </p>
        <p v-if="subtitulo" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ subtitulo }}
        </p>
      </div>
      <div v-if="icono" class="ml-4">
        <div
          class="w-12 h-12 rounded-lg flex items-center justify-center"
          :class="claseIcono"
        >
          <component :is="icono" class="w-6 h-6" />
        </div>
      </div>
    </div>

    <!-- Indicador de tendencia opcional -->
    <div v-if="tendencia" class="mt-3 flex items-center text-xs">
      <svg
        v-if="tendencia > 0"
        class="w-4 h-4 text-green-500 mr-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
      <svg
        v-else-if="tendencia < 0"
        class="w-4 h-4 text-red-500 mr-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
      <span :class="tendencia > 0 ? 'text-green-600' : tendencia < 0 ? 'text-red-600' : 'text-gray-600'">
        {{ Math.abs(tendencia) }}% vs mes anterior
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useFormatoMoneda } from '@/composables/finanzas/useFormatoMoneda'

const props = defineProps({
  etiqueta: {
    type: String,
    required: true
  },
  monto: {
    type: [Number, String],
    default: 0
  },
  moneda: {
    type: String,
    default: 'MXN'
  },
  subtitulo: {
    type: String,
    default: ''
  },
  icono: {
    type: [Object, String],
    default: null
  },
  color: {
    type: String,
    default: 'blue', // blue, green, red, yellow, purple, gray
    validator: (value) => ['blue', 'green', 'red', 'yellow', 'purple', 'gray'].includes(value)
  },
  tendencia: {
    type: Number,
    default: null
  },
  esMoneda: {
    type: Boolean,
    default: true
  }
})

const { formatearMoneda, formatearNumero, obtenerClaseColor: obtenerClaseColorMonto } = useFormatoMoneda()

const montoFormateado = computed(() => {
  if (!props.esMoneda) {
    return formatearNumero(props.monto)
  }
  return formatearMoneda(props.monto, props.moneda)
})

const obtenerClaseColor = computed(() => {
  if (props.color === 'auto') {
    return obtenerClaseColorMonto(props.monto)
  }

  const colores = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    purple: 'text-purple-600 dark:text-purple-400',
    gray: 'text-gray-900 dark:text-gray-100'
  }

  return colores[props.color] || colores.gray
})

const claseIcono = computed(() => {
  const colores = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    gray: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
  }

  return colores[props.color] || colores.gray
})
</script>
